/**
 * Error Alerting Service
 * Sends alerts when error thresholds are exceeded
 */

import { supabase } from './supabase'
import { captureMessage } from './errorTracking'

/**
 * Alert thresholds
 */
export const ALERT_THRESHOLDS = {
  ERROR_RATE: 10, // Errors per minute
  ERROR_COUNT: 50, // Total errors in time window
  CRITICAL_ERROR: 1, // Any critical error
  REPEATED_ERROR: 5, // Same error repeated
  ERROR_SPIKE: 20, // Spike in errors (2x normal rate)
}

/**
 * Alert types
 */
export const ALERT_TYPES = {
  ERROR_RATE: 'error_rate',
  ERROR_COUNT: 'error_count',
  CRITICAL_ERROR: 'critical_error',
  REPEATED_ERROR: 'repeated_error',
  ERROR_SPIKE: 'error_spike',
}

/**
 * Alert channels
 */
export const ALERT_CHANNELS = {
  EMAIL: 'email',
  SLACK: 'slack',
  WEBHOOK: 'webhook',
  IN_APP: 'in_app',
  SENTRY: 'sentry',
}

/**
 * Error alerting class
 */
class ErrorAlerter {
  constructor() {
    this.errorHistory = [] // Track errors for rate calculation
    this.alertHistory = [] // Track sent alerts
    this.alertRules = new Map() // Custom alert rules
    this.isEnabled = true
  }

  /**
   * Check if alert should be sent
   */
  async checkAlert(error, context = {}, organizationId = null) {
    if (!this.isEnabled) return null

    const now = Date.now()
    const oneMinuteAgo = now - 60000
    const oneHourAgo = now - 3600000

    // Add to history
    this.errorHistory.push({
      timestamp: now,
      error,
      context,
      fingerprint: context.fingerprint,
    })

    // Keep only last hour
    this.errorHistory = this.errorHistory.filter((e) => e.timestamp > oneHourAgo)

    // Calculate error rate (errors per minute)
    const recentErrors = this.errorHistory.filter((e) => e.timestamp > oneMinuteAgo)
    const errorRate = recentErrors.length

    // Check thresholds
    const alerts = []

    // 1. Error rate threshold
    if (errorRate >= ALERT_THRESHOLDS.ERROR_RATE) {
      const alert = await this.sendAlert(
        ALERT_TYPES.ERROR_RATE,
        {
          message: `High error rate detected: ${errorRate} errors/minute`,
          errorRate,
          threshold: ALERT_THRESHOLDS.ERROR_RATE,
          recentErrors: recentErrors.length,
        },
        organizationId
      )
      if (alert) alerts.push(alert)
    }

    // 2. Critical error
    if (context.severity === 'critical' || context.critical) {
      const alert = await this.sendAlert(
        ALERT_TYPES.CRITICAL_ERROR,
        {
          message: `Critical error detected: ${error?.message || 'Unknown error'}`,
          error: error?.message,
          stack: error?.stack,
          context,
        },
        organizationId
      )
      if (alert) alerts.push(alert)
    }

    // 3. Repeated error
    if (context.fingerprint) {
      const sameErrors = this.errorHistory.filter(
        (e) => e.fingerprint === context.fingerprint && e.timestamp > oneMinuteAgo
      )
      if (sameErrors.length >= ALERT_THRESHOLDS.REPEATED_ERROR) {
        const alert = await this.sendAlert(
          ALERT_TYPES.REPEATED_ERROR,
          {
            message: `Repeated error detected: ${sameErrors.length} occurrences`,
            fingerprint: context.fingerprint,
            error: error?.message,
            count: sameErrors.length,
          },
          organizationId
        )
        if (alert) alerts.push(alert)
      }
    }

    // 4. Error spike (compare to baseline)
    const baselineRate = this.calculateBaselineRate()
    if (errorRate >= baselineRate * 2 && errorRate >= ALERT_THRESHOLDS.ERROR_SPIKE) {
      const alert = await this.sendAlert(
        ALERT_TYPES.ERROR_SPIKE,
        {
          message: `Error spike detected: ${errorRate} errors/minute (baseline: ${baselineRate.toFixed(1)})`,
          currentRate: errorRate,
          baselineRate,
          spikeFactor: (errorRate / baselineRate).toFixed(2),
        },
        organizationId
      )
      if (alert) alerts.push(alert)
    }

    // 5. Custom alert rules
    for (const [ruleId, rule] of this.alertRules.entries()) {
      if (rule.condition(error, context, this.errorHistory)) {
        const alert = await this.sendAlert(
          rule.type || 'custom',
          {
            message: rule.message || 'Custom alert triggered',
            ruleId,
            error: error?.message,
            context,
          },
          organizationId
        )
        if (alert) alerts.push(alert)
      }
    }

    return alerts.length > 0 ? alerts : null
  }

  /**
   * Calculate baseline error rate
   */
  calculateBaselineRate() {
    if (this.errorHistory.length < 10) return 1 // Default baseline

    const oneHourAgo = Date.now() - 3600000
    const hourErrors = this.errorHistory.filter((e) => e.timestamp > oneHourAgo)
    
    // Calculate average rate per minute over the hour
    return hourErrors.length / 60
  }

  /**
   * Send alert
   */
  async sendAlert(type, data, organizationId = null) {
    const alertId = `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const alert = {
      id: alertId,
      type,
      data,
      timestamp: new Date().toISOString(),
      organizationId,
      sent: false,
    }

    // Check if similar alert was sent recently (prevent spam)
    const recentSimilar = this.alertHistory.find(
      (a) => a.type === type && Date.now() - new Date(a.timestamp).getTime() < 300000 // 5 minutes
    )

    if (recentSimilar) {
      // Update existing alert instead of sending new one
      recentSimilar.count = (recentSimilar.count || 1) + 1
      recentSimilar.lastOccurrence = alert.timestamp
      return recentSimilar
    }

    // Send alert
    try {
      // Store in database
      if (organizationId) {
        const { error } = await supabase.from('error_alerts').insert({
          organization_id: organizationId,
          alert_type: type,
          alert_data: data,
          sent_at: alert.timestamp,
        })

        if (error) {
          console.error('Failed to store alert:', error)
        }
      }

      // Send to Sentry
      captureMessage(`Alert: ${data.message}`, 'warning', {
        alertType: type,
        ...data,
      })

      // Send to configured channels
      await this.sendToChannels(alert, organizationId)

      alert.sent = true
      this.alertHistory.push(alert)

      // Keep only last 100 alerts
      if (this.alertHistory.length > 100) {
        this.alertHistory.shift()
      }

      return alert
    } catch (error) {
      console.error('Failed to send alert:', error)
      return null
    }
  }

  /**
   * Send alert to configured channels
   */
  async sendToChannels(alert, organizationId = null) {
    // Get alert configuration
    const config = this.getAlertConfig(organizationId)

    for (const channel of config.channels || []) {
      try {
        switch (channel) {
          case ALERT_CHANNELS.EMAIL:
            await this.sendEmailAlert(alert, config.email)
            break
          case ALERT_CHANNELS.SLACK:
            await this.sendSlackAlert(alert, config.slack)
            break
          case ALERT_CHANNELS.WEBHOOK:
            await this.sendWebhookAlert(alert, config.webhook)
            break
          case ALERT_CHANNELS.IN_APP:
            await this.sendInAppAlert(alert, organizationId)
            break
        }
      } catch (error) {
        console.error(`Failed to send alert via ${channel}:`, error)
      }
    }
  }

  /**
   * Get alert configuration
   */
  getAlertConfig(organizationId = null) {
    // Default configuration
    const defaultConfig = {
      channels: [ALERT_CHANNELS.IN_APP, ALERT_CHANNELS.SENTRY],
      email: null,
      slack: null,
      webhook: null,
    }

    // Load from localStorage or database
    try {
      const stored = localStorage.getItem('alert_config')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (e) {
      console.error('Failed to load alert config:', e)
    }

    return defaultConfig
  }

  /**
   * Send email alert (placeholder - implement with email service)
   */
  async sendEmailAlert(alert, emailConfig) {
    if (!emailConfig || !emailConfig.recipients) return

    // TODO: Implement email sending
    console.log('Email alert (not implemented):', alert, emailConfig)
  }

  /**
   * Send Slack alert (placeholder - implement with Slack API)
   */
  async sendSlackAlert(alert, slackConfig) {
    if (!slackConfig || !slackConfig.webhookUrl) return

    // TODO: Implement Slack webhook
    console.log('Slack alert (not implemented):', alert, slackConfig)
  }

  /**
   * Send webhook alert
   */
  async sendWebhookAlert(alert, webhookConfig) {
    if (!webhookConfig || !webhookConfig.url) return

    try {
      await fetch(webhookConfig.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(webhookConfig.headers || {}),
        },
        body: JSON.stringify(alert),
      })
    } catch (error) {
      console.error('Failed to send webhook alert:', error)
    }
  }

  /**
   * Send in-app alert (store in database)
   */
  async sendInAppAlert(alert, organizationId) {
    if (!organizationId) return

    try {
      await supabase.from('error_alerts').insert({
        organization_id: organizationId,
        alert_type: alert.type,
        alert_data: alert.data,
        sent_at: alert.timestamp,
        channel: ALERT_CHANNELS.IN_APP,
      })
    } catch (error) {
      console.error('Failed to store in-app alert:', error)
    }
  }

  /**
   * Add custom alert rule
   */
  addAlertRule(ruleId, rule) {
    this.alertRules.set(ruleId, rule)
  }

  /**
   * Remove alert rule
   */
  removeAlertRule(ruleId) {
    this.alertRules.delete(ruleId)
  }

  /**
   * Enable/disable alerting
   */
  setEnabled(enabled) {
    this.isEnabled = enabled
  }

  /**
   * Get alert history
   */
  getAlertHistory(limit = 20) {
    return this.alertHistory.slice(-limit).reverse()
  }
}

// Singleton instance
const errorAlerter = new ErrorAlerter()

/**
 * Check and send alerts for error
 */
export const checkAlert = async (error, context = {}, organizationId = null) => {
  return errorAlerter.checkAlert(error, context, organizationId)
}

/**
 * Add custom alert rule
 */
export const addAlertRule = (ruleId, rule) => {
  errorAlerter.addAlertRule(ruleId, rule)
}

/**
 * Remove alert rule
 */
export const removeAlertRule = (ruleId) => {
  errorAlerter.removeAlertRule(ruleId)
}

/**
 * Set alerting enabled
 */
export const setAlertingEnabled = (enabled) => {
  errorAlerter.setEnabled(enabled)
}

/**
 * Get alert history
 */
export const getAlertHistory = (limit = 20) => {
  return errorAlerter.getAlertHistory(limit)
}

export default errorAlerter

